import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ListFilter, X } from "lucide-react";
import { buildDanceFormLevelDetail, filterKey } from "../../lib/dashboardAnalytics";
import { ReadableNavButton } from "../Sticker";

function AnalyticsFilterLink({ active, onClick, onClear, children, testId, bold = false, clearLabel }) {
  return (
    <button
      type="button"
      onClick={() => (active ? onClear?.() : onClick())}
      aria-pressed={active}
      aria-label={active ? clearLabel || "Clear filter" : undefined}
      title={active ? "Clear this filter" : "Filter guest list"}
      data-testid={testId}
      className={`font-inherit inline-flex items-center gap-0.5 min-h-[44px] py-1 px-0.5 -mx-0.5 rounded-sm underline underline-offset-2 decoration-plum/50 hover:decoration-plum focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum ${
        active ? "text-plum font-semibold decoration-plum" : "text-plum"
      } ${bold ? "font-semibold" : ""}`}
    >
      {children}
      {active && <X className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} aria-hidden />}
    </button>
  );
}

function CohortTeaser({ segments, guestFilter, onSelectFilter, onClearFilter }) {
  if (segments.length === 0) return null;

  return (
    <ul className="space-y-0.5" data-testid="dash-analytics-teaser">
      {segments.map((seg) => (
        <li key={seg.id}>
          <AnalyticsFilterLink
            active={filterKey(guestFilter) === filterKey(seg.filter)}
            onClick={() => onSelectFilter(seg.filter)}
            onClear={onClearFilter}
            clearLabel={`Clear ${seg.label} filter`}
            testId={seg.testId}
          >
            {seg.label}
          </AnalyticsFilterLink>
        </li>
      ))}
    </ul>
  );
}

function SkillLevelList({ levelDetail, guestFilter, onSelectFilter, onClearFilter }) {
  if (levelDetail.length === 0) return null;
  return (
    <ul className="space-y-1.5 font-ui text-[14px] leading-relaxed text-ink/85 text-pretty">
      {levelDetail.map((form) => (
        <li key={form.name}>
          <AnalyticsFilterLink
            active={filterKey(guestFilter) === filterKey(form.filter)}
            onClick={() => onSelectFilter(form.filter)}
            onClear={onClearFilter}
            clearLabel={`Clear ${form.name} filter`}
            testId={form.testId}
            bold
          >
            {form.total} {form.name}
          </AnalyticsFilterLink>
          {": "}
          {form.levelParts.map((part, levelIndex) => (
            <React.Fragment key={part.level}>
              {levelIndex > 0 && ", "}
              <AnalyticsFilterLink
                active={filterKey(guestFilter) === filterKey(part.filter)}
                onClick={() => onSelectFilter(part.filter)}
                onClear={onClearFilter}
                clearLabel={`Clear ${part.label} filter`}
                testId={part.testId}
              >
                {part.label}
              </AnalyticsFilterLink>
            </React.Fragment>
          ))}
        </li>
      ))}
    </ul>
  );
}

function FilterTrigger({ menuOpen, onMenuOpenChange, hasActive }) {
  const label = hasActive ? "Filter · on" : "Filter";
  return (
    <ReadableNavButton
      type="button"
      variant={hasActive ? "primary" : "default"}
      onClick={() => onMenuOpenChange(!menuOpen)}
      className="inline-flex items-center gap-1.5 text-[13px] px-3 py-2 min-h-[44px]"
      aria-expanded={menuOpen}
      aria-haspopup="true"
      aria-controls="dash-guest-filter-menu"
      aria-label={hasActive ? "Filter on — open to change or clear" : "Filter guest list"}
      data-testid="dash-guest-filter-btn"
    >
      <ListFilter className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
      {label}
      <ChevronDown
        className={`w-4 h-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none ${menuOpen ? "rotate-180" : ""}`}
        strokeWidth={2.5}
        aria-hidden
      />
    </ReadableNavButton>
  );
}

function FilterPanel({
  teaserSegments,
  levelDetail,
  guestFilter,
  onSelectFilter,
  onClearFilter,
}) {
  const styleFilterActive =
    guestFilter?.id === "dance_form" || guestFilter?.id === "dance_level";
  const [stylesOpen, setStylesOpen] = useState(styleFilterActive);

  useEffect(() => {
    if (styleFilterActive) setStylesOpen(true);
  }, [styleFilterActive]);

  return (
    <div
      id="dash-guest-filter-menu"
      className="mt-3 rounded-md2 border border-black/[0.08] bg-white p-3 space-y-2"
      data-testid="dash-guest-filter-menu"
      role="group"
      aria-label="Guest filters"
    >
      {teaserSegments.length > 0 && (
        <>
          <CohortTeaser
            segments={teaserSegments}
            guestFilter={guestFilter}
            onSelectFilter={onSelectFilter}
            onClearFilter={onClearFilter}
          />
          {teaserSegments.some((seg) => seg.id === "first_timer") && (
            <p className="font-ui text-[12px] leading-snug text-ink/60">
              First-timers haven&apos;t attended your events before.
            </p>
          )}
        </>
      )}
      {levelDetail.length > 0 && (
        <details
          className="group"
          open={stylesOpen}
          onToggle={(e) => setStylesOpen(e.currentTarget.open)}
          data-testid="dash-filter-styles"
        >
          <summary
            aria-expanded={stylesOpen}
            className="font-ui font-medium text-[13px] text-ink/70 cursor-pointer list-none flex items-center gap-1.5 [&::-webkit-details-marker]:hidden rounded-md2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum min-h-[44px]"
          >
            By style
            <span className="font-normal text-ink/50 tabular-nums">
              · {levelDetail.length}
            </span>
            <ChevronDown
              className="w-4 h-4 text-ink/50 ml-auto transition-transform motion-reduce:transition-none group-open:rotate-180"
              strokeWidth={2.5}
              aria-hidden
            />
          </summary>
          <div className="mt-1.5 pb-0.5">
            <SkillLevelList
              levelDetail={levelDetail}
              guestFilter={guestFilter}
              onSelectFilter={onSelectFilter}
              onClearFilter={onClearFilter}
            />
          </div>
        </details>
      )}
      {teaserSegments.length === 0 && levelDetail.length === 0 && (
        <p className="font-ui text-[13px] text-ink/70">No filters for this list yet.</p>
      )}
    </div>
  );
}

/**
 * Guest list Filter control — cohorts first, styles nested under By style.
 * Render-prop children receive { trigger, panel, menuOpen } for list header layout.
 */
export default function GuestAnalyticsPanel({
  segments,
  danceFormStats,
  guestFilter,
  onSelectFilter,
  onClearFilter,
  menuOpen: controlledMenuOpen,
  onMenuOpenChange,
  children,
}) {
  const rootRef = useRef(null);
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const menuOpen = controlledMenuOpen ?? internalMenuOpen;
  const setMenuOpen = onMenuOpenChange ?? setInternalMenuOpen;
  const levelDetail = buildDanceFormLevelDetail(danceFormStats);
  const levelFormNames = new Set(levelDetail.map((form) => form.name));
  const teaserSegments = segments.filter((seg) => {
    if (!seg.id.startsWith("dance_form:")) return true;
    return !levelFormNames.has(seg.id.slice("dance_form:".length));
  });
  const hasActive = Boolean(guestFilter);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, setMenuOpen]);

  if (teaserSegments.length === 0 && levelDetail.length === 0) return null;

  const trigger = (
    <FilterTrigger
      menuOpen={menuOpen}
      onMenuOpenChange={setMenuOpen}
      hasActive={hasActive}
    />
  );

  const panel = menuOpen ? (
    <FilterPanel
      teaserSegments={teaserSegments}
      levelDetail={levelDetail}
      guestFilter={guestFilter}
      onSelectFilter={onSelectFilter}
      onClearFilter={onClearFilter}
    />
  ) : null;

  if (typeof children === "function") {
    return (
      <div ref={rootRef} data-testid="dash-analytics-panel" className="contents">
        {children({ trigger, panel, menuOpen })}
      </div>
    );
  }

  return (
    <div ref={rootRef} data-testid="dash-analytics-panel" className="w-full">
      <div className="flex justify-end">{trigger}</div>
      {panel}
    </div>
  );
}
